import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { ernaehrungAPI } from '@/lib/api';

/* Ernährung › Einstellungen (15.10.2026): Schnellvorlagen für den Kommentar zum Tag (E6b) und die Gewichtung der
   Risiko-Ampel (E12, nur Leitung). Gerechnet wird auf dem Server; hier nur Pflege und Stand des letzten Laufs.
   E21 (15.10.2026): Schwellen der Erholungs-Signale aus dem Wearable (Garmin), nur Leitung; Standard im Code.
   W2.2 (29.10.2026): Status-Schwellen, Momentum-Gewichte und Glückwunsch-Entwurf — als eigene Karte direkt unter der
   Risiko-Ampel (StatusMomentumEinstellung), damit beide Einstellungsseiten (CRM und /coach) sie ohne Änderung zeigen. */

const fehlertext = (e, rest = 'Das hat nicht geklappt') => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);
const AMPEL = { rot: 'bg-red-500', gelb: 'bg-amber-500', gruen: 'bg-emerald-500' };

export function KommentarVorlagen() {
  const [v, setV] = useState(null);
  const [neu, setNeu] = useState('');
  const [busy, setBusy] = useState(false);
  const laden = useCallback(() => ernaehrungAPI.kommentarVorlagen().then((r) => setV(r.data.vorlagen)).catch(() => {}), []);
  useEffect(() => { laden(); }, [laden]);
  if (!v) return null;
  const tu = async (fn, ok) => { setBusy(true); try { await fn(); if (ok) toast.success(ok); await laden(); } catch (e) { toast.error(fehlertext(e)); } finally { setBusy(false); } };
  const eigene = v.some((x) => x.eigen);
  return (
    <Card className="mt-4" data-testid="kommentar-vorlagen">
      <CardHeader className="pb-2"><CardTitle className="text-base">Schnellvorlagen „Kommentar zum Tag“</CardTitle></CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p className="text-xs text-muted-foreground">Im Ernährungsprotokoll mit einem Klick in den Kommentar übernommen.{!eigene && ' Bis du eigene anlegst, gelten die Startvorlagen.'}</p>
        <ul className="divide-y rounded-md border">
          {v.map((x) => <li key={x.id} className="flex items-center gap-2 p-2 text-xs"><span className="flex-1">{x.text}</span>{x.eigen && <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Vorlage löschen" disabled={busy} onClick={() => tu(() => ernaehrungAPI.kommentarVorlageLoeschen(x.id), 'Gelöscht')}><Trash2 className="h-3.5 w-3.5" /></Button>}</li>)}
        </ul>
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (neu.trim()) tu(() => ernaehrungAPI.kommentarVorlageNeu(neu.trim()).then(() => setNeu('')), 'Vorlage angelegt'); }}>
          <Input value={neu} onChange={(e) => setNeu(e.target.value)} maxLength={300} placeholder="Neue Vorlage …" className="h-9 text-sm" aria-label="Neue Vorlage" />
          <Button type="submit" size="sm" disabled={busy || neu.trim().length < 2}><Plus className="mr-1 h-4 w-4" />Anlegen</Button>
          {!eigene && <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => tu(() => ernaehrungAPI.kommentarVorlagenStart(), 'Startvorlagen übernommen')}>Startvorlagen übernehmen</Button>}
        </form>
      </CardContent>
    </Card>
  );
}

export function RisikoEinstellung() {
  const [d, setD] = useState(null);
  const [g, setG] = useState({});
  const [busy, setBusy] = useState(false);
  const laden = useCallback(() => ernaehrungAPI.risiko().then((r) => { setD(r.data); setG(r.data.gewichte); }).catch(() => {}), []);
  useEffect(() => { laden(); }, [laden]);
  if (!d) return null;
  const summe = Object.values(g).reduce((s, x) => s + (Number(x) || 0), 0);
  const tu = async (fn, ok) => { setBusy(true); try { await fn(); if (ok) toast.success(ok); await laden(); } catch (e) { toast.error(fehlertext(e)); } finally { setBusy(false); } };
  return (
    <>
    <Card className="mt-4" data-testid="risiko-einstellung">
      <CardHeader className="pb-2"><CardTitle className="text-base">Risiko-Ampel</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">Täglich je Kunde ein Wert 0–100 aus sechs Teilen, anteilig an der Summe der Gewichte. Grün unter {d.grenzen.gelb} · Gelb ab {d.grenzen.gelb} · Rot ab {d.grenzen.rot}. Rot legt eine Aufgabe auf /heute an, danach {d.cooldown_tage} Tage Ruhe je Kunde.</p>
        <div className="flex flex-wrap items-center gap-3 text-xs">
          {['rot', 'gelb', 'gruen'].map((a) => <span key={a} className="flex items-center gap-1"><span className={`h-2.5 w-2.5 rounded-full ${AMPEL[a]}`} /><span className="font-semibold tabular-nums">{d.verteilung[a]}</span> {a === 'gruen' ? 'Grün' : a === 'gelb' ? 'Gelb' : 'Rot'}</span>)}
          <span className="text-muted-foreground">{d.letzter_lauf ? `letzter Lauf ${new Date(d.letzter_lauf).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : 'noch kein Lauf'}</span>
          {d.darf_schalten && <Button size="sm" variant="outline" className="h-7" disabled={busy} onClick={() => tu(() => ernaehrungAPI.risikoLauf(), 'Lauf ausgeführt')}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Jetzt rechnen'}</Button>}
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {d.teile.map((t) => <label key={t.id} className="text-xs"><span className="block text-muted-foreground">{t.name}</span><span className="flex items-center gap-1"><Input type="number" min={0} max={100} value={g[t.id] ?? ''} disabled={!d.darf_schalten} onChange={(e) => setG({ ...g, [t.id]: e.target.value === '' ? '' : Number(e.target.value) })} className="h-8 w-20 text-sm" aria-label={t.name} /><span>%</span></span></label>)}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className={summe === 100 ? 'text-muted-foreground' : 'text-amber-800'}>Summe {summe} % {summe !== 100 && '— es wird anteilig gerechnet'}</span>
          {d.darf_schalten ? <Button size="sm" className="ml-auto h-7" disabled={busy} onClick={() => tu(() => ernaehrungAPI.risikoGewichte(g), 'Gewichtung gespeichert')}>Speichern</Button> : <span className="ml-auto text-muted-foreground">Die Gewichtung ändert nur die Leitung.</span>}
        </div>
        {d.kunden.length > 0 && <ul className="divide-y rounded-md border text-xs">
          {d.kunden.slice(0, 10).map((k) => <li key={k.lead_id} className="flex items-center gap-2 p-2"><span className={`h-2.5 w-2.5 shrink-0 rounded-full ${AMPEL[k.ampel]}`} /><Link to={`/coaching-cockpit?kunde=${k.lead_id}`} className="font-medium hover:underline">{k.name}</Link><span className="tabular-nums">{k.wert}</span><span className="min-w-0 flex-1 truncate text-muted-foreground">{(k.gruende || []).join(' · ')}</span></li>)}
        </ul>}
      </CardContent>
    </Card>
    <StatusMomentumEinstellung />
    </>
  );
}


/* W2.2: Status & Momentum aller Coaching-Kunden (GET|PUT /api/ernaehrung/risiko/status-einstellungen). Gerechnet wird im
   täglichen Lauf der Risiko-Ampel; hier nur Pflege (Leitung) und die Verteilung des letzten Laufs. */
const STATUS_FARBE = { auf_kurs: 'bg-emerald-500', stabil: 'bg-sky-500', beobachten: 'bg-amber-500', kritisch: 'bg-red-500', daten_fehlen: 'bg-muted-foreground' };

export function StatusMomentumEinstellung() {
  const [d, setD] = useState(null);
  const [e, setE] = useState(null);
  const [busy, setBusy] = useState(false);
  const laden = useCallback(() => ernaehrungAPI.risikoStatusEinstellungen().then((r) => { setD(r.data); setE(r.data.einstellungen); }).catch(() => {}), []);
  useEffect(() => { laden(); }, [laden]);
  if (!d || !e) return null;
  const darf = d.darf_schalten;
  const zahl = (v) => (v === '' ? '' : Number(v));
  const setzen = (feld, wert) => setE({ ...e, [feld]: wert });
  const unter = (gruppe, feld, wert) => setE({ ...e, [gruppe]: { ...e[gruppe], [feld]: wert } });
  const speichern = async () => {
    setBusy(true);
    try {
      const daten = { schwellen: e.schwellen, momentum_gewichte: e.momentum_gewichte, risiko_training: e.risiko_training,
        gewicht_schritt_kg: e.gewicht_schritt_kg, training_soll_je_woche: e.training_soll_je_woche, glueckwunsch_entwurf: e.glueckwunsch_entwurf };
      await ernaehrungAPI.risikoStatusEinstellungenSetzen(daten);
      toast.success('Status & Momentum gespeichert');
      await laden();
    } catch (err) { toast.error(fehlertext(err)); } finally { setBusy(false); }
  };
  const st = d.statistik;
  const feld = (label, wert, onChange, props = {}) => (
    <label className="text-xs"><span className="block text-muted-foreground">{label}</span>
      <Input type="number" value={wert ?? ''} disabled={!darf} onChange={(ev) => onChange(zahl(ev.target.value))} className="h-11 w-24 text-sm sm:h-8" aria-label={label} {...props} /></label>
  );
  return (
    <Card className="mt-4" data-testid="status-momentum-einstellung">
      <CardHeader className="pb-2"><CardTitle className="text-base">Status &amp; Momentum (alle Coaching-Kunden)</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">Derselbe tägliche Lauf bewertet alle Kunden mit Kraftlog-Plan, Ernährung, PT oder Phase. Der Status folgt dem Risikowert; „Daten fehlen“, wenn kein Momentum-Teil einen Wert hat. Kritisch legt eine Aufgabe an (danach 7 Tage Ruhe). Momentum 0–100 rechnet nur mit den Teilen, die es für den Kunden gibt.</p>
        {st && <div className="flex flex-wrap items-center gap-3 text-xs">
          {d.status.map((s) => <span key={s.id} className="flex items-center gap-1"><span className={`h-2.5 w-2.5 rounded-full ${STATUS_FARBE[s.id]}`} /><span className="font-semibold tabular-nums">{st.status?.[s.id] ?? 0}</span> {s.name}</span>)}
          <span className="text-muted-foreground">{st.bewertet} bewertet am {st.tag ? new Date(st.tag).toLocaleDateString('de-DE') : '–'}</span>
        </div>}
        <div>
          <p className="mb-1 text-xs font-medium">Status-Schwellen (Risikowert)</p>
          <div className="flex flex-wrap gap-3">
            {feld('Auf Kurs unter', e.schwellen.auf_kurs, (v) => unter('schwellen', 'auf_kurs', v), { min: 1, max: 100 })}
            {feld('Stabil unter', e.schwellen.stabil, (v) => unter('schwellen', 'stabil', v), { min: 1, max: 100 })}
            {feld('Kritisch ab', e.schwellen.beobachten, (v) => unter('schwellen', 'beobachten', v), { min: 1, max: 100 })}
          </div>
        </div>
        <div>
          <p className="mb-1 text-xs font-medium">Gewichte im Momentum</p>
          <div className="flex flex-wrap gap-3">
            {d.teile.map((t) => <span key={t.id}>{feld(t.name, e.momentum_gewichte[t.id], (v) => unter('momentum_gewichte', t.id, v), { min: 0, max: 100 })}</span>)}
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          {feld('Risiko-Teil Training (Gewicht)', e.risiko_training, (v) => setzen('risiko_training', v), { min: 0, max: 100 })}
          {feld('Gewichtsmeilenstein alle … kg', e.gewicht_schritt_kg, (v) => setzen('gewicht_schritt_kg', v), { min: 0.5, max: 10, step: 0.5 })}
          {feld('Trainingstage je Woche = 100 %', e.training_soll_je_woche, (v) => setzen('training_soll_je_woche', v), { min: 1, max: 7 })}
        </div>
        <label className="flex min-h-11 items-center gap-3 text-xs">
          <Switch checked={!!e.glueckwunsch_entwurf} disabled={!darf} onCheckedChange={(v) => setzen('glueckwunsch_entwurf', v)} aria-label="Glückwunsch-Entwurf bei Meilensteinen" />
          <span>Bei einem Meilenstein einen Glückwunsch als Entwurf in die Freigabe-Zentrale legen (Coach-Chat, nichts geht automatisch raus)</span>
        </label>
        <div className="flex items-center gap-2 text-xs">
          {darf ? <Button size="sm" className="ml-auto h-11 sm:h-7" disabled={busy} onClick={speichern}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Speichern'}</Button>
            : <span className="ml-auto text-muted-foreground">Status und Momentum stellt nur die Leitung ein.</span>}
        </div>
      </CardContent>
    </Card>
  );
}


/* E21: Schwellen der drei Erholungs-Signale (GET|PUT /api/ernaehrung/wearable/schwellen). Der Server rechnet die Signale beim
   Lesen; hier wird nur gepflegt. Ohne Garmin-Schlüssel in der Umgebung zeigt die Karte den Stand „nicht freigeschaltet“. */
export function WearableSchwellen() {
  const [d, setD] = useState(null);
  const [s, setS] = useState({});
  const [busy, setBusy] = useState(false);
  const laden = useCallback(() => ernaehrungAPI.wearableSchwellen().then((r) => { setD(r.data); setS(r.data.schwellen); }).catch(() => {}), []);
  useEffect(() => { laden(); }, [laden]);
  if (!d) return null;
  const speichern = async () => {
    setBusy(true);
    try { await ernaehrungAPI.wearableSchwellenSetzen(s); toast.success('Schwellen gespeichert'); await laden(); }
    catch (e) { toast.error(fehlertext(e)); } finally { setBusy(false); }
  };
  const gruppen = [
    ['Möglicherweise angeschlagen', ['ruhepuls_plus', 'hrv_minus_prozent', 'angeschlagen_tage']],
    ['Erholung niedrig', ['stress_ab', 'stress_tage', 'body_battery_unter', 'body_battery_tage']],
    ['Nach Alkohol (nur mit gemeldetem Alkohol)', ['schlaf_minus_prozent', 'alkohol_ruhepuls_plus']],
  ];
  return (
    <Card className="mt-4" data-testid="wearable-schwellen">
      <CardHeader className="pb-2"><CardTitle className="text-base">Erholung (Garmin) — Schwellen der Signale</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">
          Hinweise, keine Diagnosen. Je Kunde gilt der eigene Basiswert (Median der letzten {d.basis_tage} Tage, mindestens {d.basis_min_tage} Tage mit Werten) — vorher kein Signal.
          {' '}{d.verfuegbar ? `${d.verbunden} Garmin-Verbindung${d.verbunden === 1 ? '' : 'en'} aktiv.` : 'Garmin-Schlüssel fehlen in der Umgebung — die App zeigt bis zur Freigabe den Übergang über Apple Health.'}
        </p>
        {gruppen.map(([titel, felder]) => (
          <div key={titel}>
            <p className="mb-1 text-xs font-medium">{titel}</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {felder.map((k) => <label key={k} className="text-xs"><span className="block text-muted-foreground">{d.namen[k]}</span>
                <Input type="number" min={d.grenzen[k][0]} max={d.grenzen[k][1]} value={s[k] ?? ''} disabled={!d.darf_schalten}
                  onChange={(e) => setS({ ...s, [k]: e.target.value === '' ? '' : Number(e.target.value) })} className="h-8 w-24 text-sm" aria-label={d.namen[k]} />
                <span className="block text-[11px] text-muted-foreground">Standard {d.standard[k]}</span></label>)}
            </div>
          </div>
        ))}
        <div className="flex items-center gap-2 text-xs">
          {d.darf_schalten ? <Button size="sm" className="ml-auto h-7" disabled={busy} onClick={speichern}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Speichern'}</Button>
            : <span className="ml-auto text-muted-foreground">Die Schwellen ändert nur die Leitung.</span>}
        </div>
      </CardContent>
    </Card>
  );
}
